import { useQuery } from '@tanstack/react-query';
import * as Combobox from '../../components/Combobox';
import { useRouteContext, useSearch } from '@tanstack/react-router';
import { Destination } from '../../services/DestinationsApiClient';
import { YoutubeApiClient } from '../../services/YouTube';
import { Box, Divider, Text, TextField } from '@streamelements/frontend-ui';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '.';
import { forwardRef } from 'react';

type Props = {
  output: Destination<'youtube'>['outputs'][number];
  destinationMetadata: Destination<'youtube'>;
};

function YouTubeStreamTitleField({ output }: { output: Destination<'youtube'>['outputs'][number] }) {
  const { formState, watch, setValue } = useFormContext<FormValues>();
  const { errors } = formState;
  const value = watch(`outputs.${output.id}.title`);

  return (
    <TextField.Single
      label="Stream title"
      name={`outputs.${output.id}.title` as const}
      defaultValue={value}
      onChange={(e) =>
        setValue(`outputs.${output.id}.title` as const, e.target.value, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
      }
      error={!!errors?.outputs?.[output.id]?.title?.message}
      css={{ width: '100%' }}
    >
      {errors?.outputs?.[output.id]?.title?.message && (
        <TextField.HelperText css={{ color: '$textError' }}>
          {errors?.outputs?.[output.id]?.title?.message}
        </TextField.HelperText>
      )}
    </TextField.Single>
  );
}

export const YouTubeBroadcastTitle = forwardRef<unknown, Props>(function YouTubeBroadcastTitle(props, ref) {
  void ref;
  const { output, destinationMetadata } = props;
  const { mode } = useSearch({
    from: '/_authenticated/settings/_layout/broadcast-settings/',
  });
  const form = useFormContext<FormValues>();
  const { authContextProvider, serviceEnvironment } = useRouteContext({
    from: '/_authenticated/settings/_layout/broadcast-settings/',
  });

  const value = form.watch(`outputs.${output.id}.broadcastId`) ?? '';

  const { data: broadcasts = new Map() as never, isLoading } = useQuery({
    queryKey: ['youtube', 'broadcasts', destinationMetadata.accessToken],
    queryFn: async () => {
      const youtubeApiClient = new YoutubeApiClient({
        authContextProvider,
        serviceEnvironment,
        destinationMetadata,
      });

      const result = await youtubeApiClient.fetchBroadcasts({
        status: 'upcoming',
      });

      const resultMap = new Map(result.map((b) => [b.id, b]));

      return resultMap;
    },
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: Infinity,
  });

  const siblingValues = destinationMetadata.outputs.map((o) => o.id).filter((id) => id !== output.id);
  const siblingBroadcasts = new Set(
    siblingValues
      .map((siblingId) => form.watch(`outputs.${siblingId}.broadcastId`))
      .filter((value) => {
        if (value === 'new') return false;
        return !!value;
      })
  );
  const broadcast = broadcasts.get(value);
  const availableBroadcasts = broadcasts.size - siblingBroadcasts.size;

  if (mode === 'streams:update') {
    return <YouTubeStreamTitleField output={output} />;
  }

  if (availableBroadcasts <= 0) {
    return <YouTubeStreamTitleField output={output} />;
  }

  return (
    <>
      <Combobox.ComboBoxRoot
        inputValue={
          isLoading ? 'Loading...' : value === 'new' ? 'Create a new broadcast' : (broadcast?.snippet?.title ?? '')
        }
        value={[value]}
        onValueChange={([next]) => {
          const nextValue = next || value;
          form.setValue(`outputs.${output.id}.broadcastId` as const, nextValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });

          form.setValue(
            `outputs.${output.id}.title` as const,
            broadcasts.get(nextValue)?.snippet?.title ?? output.auxiliaryData.streamTitle ?? 'New broadcast',
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }}
      >
        <Combobox.ComboBoxTrigger
          label="Broadcast"
          name={`outputs.${output.id}.broadcastId`}
          inputProps={{ readOnly: true }}
        />
        <Combobox.ComboBoxContent>
          <Combobox.ComboBoxOption value="new">Create a new broadcast</Combobox.ComboBoxOption>

          <Divider css={{ my: '0.125rem' }} />

          {Array.from(broadcasts.values()).map(({ id, snippet: { title } }) => {
            if (siblingBroadcasts.has(id)) return null;

            return (
              <Combobox.ComboBoxOption
                key={id}
                value={id}
                label={title}
                css={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <Text.Body>{title}</Text.Body>
              </Combobox.ComboBoxOption>
            );
          })}
        </Combobox.ComboBoxContent>
      </Combobox.ComboBoxRoot>
      {value === 'new' && (
        <>
          <Box css={{ mb: '1rem' }} />
          <YouTubeStreamTitleField output={output} />
        </>
      )}
    </>
  );
});
